import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Grid,
  Skeleton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DOMAIN } from '../../config';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { convertSecondsToTime,formatViews } from '../../funcs';
dayjs.extend(relativeTime);



// 单个视频卡片组件
const VideoCard = ({ thumbnail, duration, title, date, views, id, onClick }) => {
  const formattedDuration = convertSecondsToTime(Number(duration));
  const formattedViews = formatViews(views);

  return (
    <Card
      onClick={() => onClick?.(id)}
      sx={{
        width: '100%',
        borderRadius: 3,
        boxShadow: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.3s',
        '&:hover': { transform: 'translateY(-5px)' },
      }}
    >
      <Box sx={{ position: 'relative', aspectRatio: '16/9' }}>
        <CardMedia
          component="img"
          src={`${DOMAIN}miniapp/${thumbnail}.tb.jpg`}
          alt="thumbnail"
          onError={(e) => {
            const img = e.target;
            img.onerror = null; // 防止重复触发
            img.src = `${DOMAIN}miniapp/${thumbnail}.jpg`; // 切换备用图
          }}
          sx={{ width: '100%', height: '100%', objectFit: 'cover',borderRadius: 2 }}
        />


        {/* 视频时长 */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: 12,
            px: 0.8,
            py: 0.3,
            m: 1,
            borderRadius: 1,
          }}
        >
          {formattedDuration}
        </Box>

        {/* 观看次数 */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: 12,
            px: 0.8,
            py: 0.3,
            m: 1,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
          }}
        >
          <VisibilityIcon sx={{ fontSize: 14 }} />
          <span>{formattedViews}</span>
        </Box>
      </Box>

      <CardContent sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            fontSize: '0.85rem',
            color: 'rgba(0,0,0,0.7)',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontSize: '0.75rem' }}
        >
          {dayjs(date * 1000).fromNow()}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Skeleton 卡片
const VideoSkeleton = () => (
  <Card sx={{ width: '100%', borderRadius: 3, boxShadow: 3 }}>
    <Skeleton variant="rectangular" sx={{ aspectRatio: '16/9' }} />
    <Box sx={{ p: 2 }}>
      <Skeleton height={20} width="80%" />
      <Skeleton height={18} width="60%" sx={{ mt: 0.5 }} />
    </Box>
  </Card>
);

// 视频列表组件
export default function VideoGallery({ videos: fetchVideos, handleChangeVideoid, videoid }) {
  const [videos, setVideos] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const debounceRef = useRef(false);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    const newVideos = await fetchVideos(page, 6);
    if (newVideos.length > 0) {
      setVideos((prev) => [...prev, ...newVideos]);
      setPage((prev) => prev + 1);
    } else {
      setHasMore(false);
    }
    setLoading(false);
  }, [page, loading, hasMore, fetchVideos]);

  // 当videoid变化时重置
  useEffect(() => {
    setVideos([]);
    setPage(0);
    setHasMore(true);
    loadMore(); // 初次加载
  }, [videoid]);

  // 监听底部滚动触发懒加载
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !debounceRef.current) {
          debounceRef.current = true;
          setTimeout(() => (debounceRef.current = false), 500); // 防抖500ms
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 300px 0px', // 提前200px加载
        threshold: 0,
      }
    );

    const loader = loaderRef.current;
    if (loader) observer.observe(loader);

    return () => {
      if (loader) observer.unobserve(loader);
    };
  }, [loadMore]);

  return (
    <Box
      sx={{
        px: 1,
        py: 2,
        width: '95%',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: 'rgba(0, 0, 0, 0.65)',
          userSelect: 'none',
        }}
      >
        您可能喜欢
      </Typography>

      <Grid container spacing={2}>
        {videos.map((video, index) => (
          <Grid item key={index} xs={6} sm={6} md={4} lg={3}>
            <VideoCard {...video} onClick={(vid) => handleChangeVideoid?.(vid,video.thumbnail)} />
          </Grid>
        ))}

        {loading &&
          [...Array(4)].map((_, index) => (
            <Grid item key={`skeleton-${index}`} xs={6} sm={6} md={4} lg={3}>
              <VideoSkeleton />
            </Grid>
          ))}
      </Grid>

      {hasMore && <div ref={loaderRef} style={{ height: '1px' }} />}
    </Box>
  );
}
